
import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import type { Canvas } from '@/hooks/useCanvas'
import { useAuth } from '@/hooks/useAuth'
import { useThemeMode } from '@/lib/stores/dark-mode-store'
import type { Node, Edge } from '@xyflow/react'
import { apiCall } from '@/lib/api/api-helpers'
import type { LoadedCanvasPage } from '@/types/canvas'

type Viewport = { x: number; y: number; zoom: number }
import { filterNodesTransientState } from '@/utils/canvas/nodeStateFilter'
import { sortParentsFirst } from '@/lib/canvas/sort-parents-first'
import { getViewportFromLocalStorage } from '@/utils/canvas/viewportCache'
import { prefetchLibraryModels } from '@/lib/canvas/utils/prefetchCanvasData'
import { initializeCountersFromNodes } from '@/lib/canvas/utils/nodeFactory'
import { hydrateSavedNodeBox } from '@/lib/canvas/node-box'
import { useSeriesDataStore } from '@/stores/useSeriesDataStore'

/**
 * Canvas page state hook
 * Handles canvas fetching, authentication, and state management
 */
export function useCanvasPage() {
  const { id: canvasId } = useParams({ strict: false }) as { id: string }
  const navigate = useNavigate()

  // State
  const [currentCanvas, setCurrentCanvas] = useState<Canvas | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [mounted, setMounted] = useState(false)

  // Pages state
  const [pages, setPages] = useState<Array<{ id: string; uuid: string; title: string; order: number }>>([])
  const [activePage, setActivePage] = useState<string | null>(null)
  const [isPagesLoading, setIsPagesLoading] = useState(false)
  // Page whose content is currently held in `nodes`/`edges`. Set in the same
  // batch as the hydrated nodes/edges so the save system can bind saves to the
  // page the content belongs to (and skip saving while a page is hydrating).
  const [loadedPage, setLoadedPage] = useState<LoadedCanvasPage | null>(null)

  // Magic Canvas state — use cached viewport for instant restore, fallback to default
  const [viewport, setViewport] = useState<Viewport>(
    () => getViewportFromLocalStorage(canvasId) ?? { x: 0, y: 0, zoom: 1 }
  )
  // Guard: skip API viewport override if user has already interacted
  const userHasInteractedRef = useRef(false)
  const [nodes, setNodes] = useState<Node[]>([])
  const [edges, setEdges] = useState<Edge[]>([])
  const [selectedNodes, setSelectedNodes] = useState<string[]>([])

  // Shared dark mode store (single MutationObserver for all consumers)
  const theme = useThemeMode()

  // Hooks
  const { isAuthenticated, isLoading: authLoading } = useAuth()

  // Direct API call to fetch a single canvas by UUID (avoids useCanvas() which loads ALL canvases)
  const getCanvasDirect = useCallback(async (uuid: string): Promise<Canvas | null> => {
    try {
      const result = await apiCall(`/api/canvas/${uuid}`, true) as { success: boolean; data: Canvas; error?: string }
      if (result.success && result.data) {
        return result.data
      }
      throw new Error(result.error || 'Failed to get canvas')
    } catch (err) {
      console.error('Failed to get canvas:', err)
      return null
    }
  }, [])

  // Store refs for stable callbacks
  const getCanvasRef = useRef(getCanvasDirect)
  const isAuthenticatedRef = useRef(isAuthenticated)
  const authLoadingRef = useRef(authLoading)
  const navigateRef = useRef(navigate)

  // Update refs when values change
  useEffect(() => {
    getCanvasRef.current = getCanvasDirect
    isAuthenticatedRef.current = isAuthenticated
    authLoadingRef.current = authLoading
    navigateRef.current = navigate
  })

  // Theme is provided by shared dark-mode-store (single MutationObserver)

  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true)
  }, [])

  // Fetch pages for canvas
  const fetchPages = useCallback(async (canvasInternalId: string) => {
    setIsPagesLoading(true)
    try {
      const result = await apiCall(`/api/canvas/${canvasInternalId}/pages`, true) as {
        success: boolean;
        data: Array<{ id: string; uuid: string; title: string; order: number }>;
      }
      if (result.success && result.data) {
        const pagesList = result.data.sort((a: { order: number }, b: { order: number }) => a.order - b.order)
        setPages(pagesList)

        // Auto-select first page (use callback to get current activePage)
        setActivePage(current => {
          if (!current && pagesList.length > 0) {
            return pagesList[0].id
          }
          return current
        })
      }
    } catch (error) {
      console.error('Failed to fetch pages:', error)
    } finally {
      setIsPagesLoading(false)
    }
  }, []) // No dependencies - stable callback

  // Fetch canvas - stable callback using refs (no dependencies except canvasId)
  const fetchCanvas = useCallback(async () => {
    if (canvasId) {
      setIsLoading(true)
      try {
        const canvas = await getCanvasRef.current(canvasId)
        setCurrentCanvas(canvas)
        // Fetch pages after canvas is loaded
        if (canvas?.id) {
          await fetchPages(canvas.id)
        }
        // Prefetch library models in background
        prefetchLibraryModels()
      } catch (error) {
        console.error('Failed to fetch canvas:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }, [canvasId, fetchPages])

  // Fetch when auth is ready - check auth state here
  useEffect(() => {
    if (authLoading) return // Wait for auth to load

    if (!isAuthenticated) {
      navigate({ to: '/login' })
      return
    }

    fetchCanvas()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, canvasId]) // Don't depend on fetchCanvas to avoid loop

  // Load active page's nodes/edges when page changes.
  // Keyed on the page id + uuid (not the whole pages array) so refetching the
  // page list does not re-hydrate and clobber in-progress local edits.
  const activePageUuid = pages.find(p => p.id === activePage)?.uuid ?? null
  useEffect(() => {
    if (activePage && activePageUuid) {
      const pageId = activePage
      const pageUuid = activePageUuid
      // Ignore responses for a page the user already navigated away from
      let cancelled = false
      // Fetch page data
      const fetchPageData = async () => {
        try {
          const result = await apiCall(`/api/canvas/pages/${pageUuid}`, true) as {
            success: boolean;
            data: {
              nodes?: Node[];
              edges?: Edge[];
              config?: { viewport?: Viewport } & Record<string, unknown>;
              revision?: number;
            };
          }
          if (cancelled) return
          if (result.success && result.data) {
            // Load nodes (filter transient state on load)
            if (result.data.nodes) {
              const cleanNodes = filterNodesTransientState(result.data.nodes as Node[])
                // Strip top-level `handles` from persisted nodes.
                // React Flow v12's declarative handles API expects { type: 'source'|'target' },
                // but our HandleMeta uses { type: 'input'|'output' }. If top-level handles
                // exist, React Flow skips DOM-measured handleBounds and uses the broken
                // declarative handles, producing "Couldn't create edge" warnings.
                // Our handles live in data.handles and are rendered via <NodeHandles>.
                .map(node => {
                  const copy = { ...node } as Node & { handles?: unknown }
                  delete copy.handles
                  return hydrateSavedNodeBox(copy)
                })
              setNodes(sortParentsFirst(cleanNodes))

              // Hydrate external series data store from persisted node state
              useSeriesDataStore.getState().hydrateFromNodes(
                cleanNodes.map(n => ({
                  id: n.id,
                  type: n.type,
                  data: n.data as Record<string, unknown>,
                }))
              )

              // Initialize node title counters from existing nodes
              // so new nodes continue numbering (e.g., "Image Create 4" after existing 1-3)
              initializeCountersFromNodes(cleanNodes.map(n => ({
                title: (n.data as Record<string, unknown>)?.state
                  ? ((n.data as Record<string, unknown>).state as Record<string, unknown>)?.title as string | undefined
                  : undefined,
                type: n.type,
                purpose: (n.data as Record<string, unknown>)?.state
                  ? ((n.data as Record<string, unknown>).state as Record<string, unknown>)?.purpose as string | undefined
                  : undefined,
              })))
            } else {
              setNodes([])
            }

            // Load edges
            if (result.data.edges) {
              setEdges(result.data.edges as Edge[])
            } else {
              setEdges([])
            }

            // Mark this page's content as loaded (batched with setNodes/setEdges).
            // The revision travels with it: saves are refused unless they say
            // which version of the page they were built on.
            setLoadedPage({ pageId, uuid: pageUuid, revision: result.data.revision ?? 0 })

            // Load viewport from config (skip if user already interacted)
            if (!userHasInteractedRef.current) {
              if (result.data.config && typeof result.data.config === 'object') {
                const config = result.data.config as { viewport?: Viewport }
                if (config.viewport) {
                  setViewport(config.viewport)
                } else {
                  setViewport({ x: 0, y: 0, zoom: 1 })
                }
              } else {
                setViewport({ x: 0, y: 0, zoom: 1 })
              }
            }
          }
        } catch (error) {
          console.error('Failed to fetch page data:', error)
        }
      }

      fetchPageData()
      return () => {
        cancelled = true
      }
    }
  }, [activePage, activePageUuid])

  // Update local canvas state (for optimistic updates)
  const updateLocalCanvas = useCallback((updates: Partial<Canvas>) => {
    setCurrentCanvas(prev => prev ? { ...prev, ...updates } : null)
  }, [])

  return {
    // Canvas data
    currentCanvas,
    canvasId,

    // Pages data
    pages,
    activePage,
    setActivePage,
    isPagesLoading,
    refetchPages: () => currentCanvas?.id && fetchPages(currentCanvas.id),
    loadedPage,

    // Loading states
    isLoading,
    authLoading,
    mounted,

    // Magic Canvas state
    viewport,
    setViewport,
    markViewportInteracted: () => { userHasInteractedRef.current = true },
    nodes,
    setNodes,
    edges,
    setEdges,
    theme,
    selectedNodes,
    setSelectedNodes,

    // Actions
    refetch: fetchCanvas,
    updateLocalCanvas,
  }
}
